import {Product, User} from '@hconnect/apiclient'
import {BoxedContainer} from '@hconnect/uikit'
import {Alert, H5, Icon} from '@hm/fundamental'
import {Box, Paper, PaperProps, Typography} from '@material-ui/core'
import {makeStyles} from '@material-ui/core/styles'
import InfoRoundedIcon from '@material-ui/icons/InfoRounded'
import {mdiAlertOutline} from '@mdi/js'
import React from 'react'

import {Permission} from '../../modules/LoggedInUser'

import {InviteUserButtons} from './InviteUserButtons'

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: `0 ${theme.spacing(2)}px`,
    zIndex: 10
  },
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    minHeight: theme.spacing(6),
    alignItems: 'center',
    padding: `${theme.spacing(1)}px 0`
  },
  colorPrimary: {
    color: 'red'
  },
  icon: {
    marginRight: 10
  },
  body1: {
    fontSize: 14
  },
  info: {
    marginRight: 6
  }
}))

interface Props extends PaperProps {
  info: string
  action: string
  user?: User | null
  products?: Product[]
  extraLines?: Array<{
    title: string
    description: string
  }>
  showNewUserProfile?: boolean
  refetchUser?: () => void
  loggedInUserPermissions?: Permission[] | null
}

export const UserIssueBar: React.FunctionComponent<Props> = ({
  info,
  action,
  extraLines = [],
  showNewUserProfile,
  ...otherProps
}) => {
  const classes = useStyles()
  const lines = [{title: info, description: action}].concat(extraLines)

  if (showNewUserProfile) {
    return (
      <Alert variant="warning">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Icon path={mdiAlertOutline} className="text-warning" />
            <H5>{info}</H5>
          </div>
          <div className="flex items-center gap-2">
            <span>{action}</span>
            {otherProps.products && (
              <InviteUserButtons
                data-test-id="invite-buttons-box-notification"
                variant="text"
                user={otherProps.user}
                products={otherProps.products}
                refetchUser={otherProps.refetchUser}
                loggedInUserPermissions={otherProps.loggedInUserPermissions}
                showNewUserProfile={showNewUserProfile}
              />
            )}
          </div>
          {extraLines.length > 0 &&
            extraLines.map((line) => (
              <div key={line.title} className="flex gap-2">
                <span>{line.title}</span>
                <span>{line.description}</span>
              </div>
            ))}
        </div>
      </Alert>
    )
  }

  return (
    <Paper className={classes.paper} square elevation={4} {...otherProps}>
      <BoxedContainer>
        <div className={classes.root}>
          <InfoRoundedIcon
            color="primary"
            className={classes.icon}
            classes={{colorPrimary: classes.colorPrimary}}
          />
          <div>
            {lines.map(({title, description}) => (
              <Box display="flex" alignItems="center" key={title}>
                <Typography className={classes.info} variant="subtitle1">
                  {title}
                </Typography>
                <Typography classes={{body1: classes.body1}} variant="body1">
                  {description}
                </Typography>
              </Box>
            ))}
          </div>
        </div>
      </BoxedContainer>
    </Paper>
  )
}
